/**
 * NotificationService
 *
 * Handles notification operations, ensuring all notifications are associated with the current authenticated user.
 * Provides methods for creating, updating, deleting, and retrieving user-specific notifications.
 * Integrates with BaseRepository and DatabaseService for persistence.
 * Includes desktop notification support and notification cleanup functionality.
 *
 * @module NotificationService
 */
import BaseRepository from './BaseRepository';
import { DatabaseService } from './DatabaseService';
import AuthService from './AuthService';
import { validateNotification } from '../models/AlertModels';

const NOTIFICATION_STORE = 'notifications';

// Configuration constants
const MAX_NOTIFICATIONS = 100; // Maximum number of notifications to keep
const NOTIFICATION_RETENTION_DAYS = 30; // Number of days to keep notifications

class NotificationService {
  /**
   * @param {DatabaseService} dbService - Instance of DatabaseService
   */
  constructor(dbService) {
    this.dbService = dbService || new DatabaseService('DeepRDashboardDB', 2, { notifications: { keyPath: 'id' } });
    this.repository = new BaseRepository(this.dbService, NOTIFICATION_STORE);
    this.desktopNotificationsEnabled = this.checkNotificationPermission() === 'granted';
  }

  /**
   * Create a new notification associated with the current user
   * @param {Object} notification - Notification object
   * @returns {Promise<IDBValidKey>}
   */
  async createNotification(notification) {
    const user = AuthService.getPostAuthData()?.userData;
    if (!user || !user.user_id) {
      throw new Error('User context is required to create a notification');
    }
    const notificationWithUser = { ...notification, user_id: user.user_id };
    return this.repository.create(notificationWithUser);
  }

  /**
   * Retrieve all notifications for the current user
   * @returns {Promise<Object[]>}
   */
  async getUserNotifications() {
    const user = AuthService.getPostAuthData()?.userData;
    if (!user || !user.user_id) {
      throw new Error('User context is required to fetch notifications');
    }
    const allNotifications = await this.repository.findAll();
    return allNotifications.filter(n => n.user_id === user.user_id);
  }

  /**
   * Mark a notification as read (must belong to the current user)
   * @param {string} notificationId
   * @returns {Promise<Object>}
   */
  async markAsRead(notificationId) {
    const user = AuthService.getPostAuthData()?.userData;
    if (!user || !user.user_id) {
      throw new Error('User context is required to update a notification');
    }
    const allNotifications = await this.repository.findAll();
    const notification = allNotifications.find(n => n.id === notificationId && n.user_id === user.user_id);
    if (!notification) {
      throw new Error('Notification not found or does not belong to the current user');
    }
    const updatedNotification = { ...notification, read: true };
    await this.repository.update(notificationId, updatedNotification);
    return updatedNotification;
  }

  /**
   * Delete a notification (must belong to the current user)
   * @param {string} notificationId
   * @returns {Promise<boolean>}
   */
  async deleteNotification(notificationId) {
    const user = AuthService.getPostAuthData()?.userData;
    if (!user || !user.user_id) {
      throw new Error('User context is required to delete a notification');
    }
    const allNotifications = await this.repository.findAll();
    const notification = allNotifications.find(n => n.id === notificationId && n.user_id === user.user_id);
    if (!notification) {
      throw new Error('Notification not found or does not belong to the current user');
    }
    await this.repository.delete(notificationId);
    return true;
  }

  /**
   * Mark all notifications as read for the current user
   * @returns {Promise<boolean>}
   */
  async markAllAsRead() {
    const user = AuthService.getPostAuthData()?.userData;
    if (!user || !user.user_id) {
      throw new Error('User context is required to update notifications');
    }
    
    const userNotifications = await this.getUserNotifications();
    const unreadNotifications = userNotifications.filter(n => !n.read);
    
    // If there are no unread notifications, return early
    if (unreadNotifications.length === 0) {
      return true;
    }
    
    // Update each unread notification
    for (const notification of unreadNotifications) {
      const updatedNotification = { ...notification, read: true };
      await this.repository.update(notification.id, updatedNotification);
    }

    // Broadcast an update after marking all as read
    try {
      const channel = new BroadcastChannel('notification-updates');
      channel.postMessage({ type: 'ALL_NOTIFICATIONS_MARKED_AS_READ' });
      channel.close();
    } catch (error) {
      console.error('NotificationService: Error broadcasting ALL_NOTIFICATIONS_MARKED_AS_READ:', error);
    }
    
    return true;
  }
  
  /**
   * Delete all notifications for the current user
   * @returns {Promise<boolean>}
   */
  async deleteAllNotifications() {
    const user = AuthService.getPostAuthData()?.userData;
    if (!user || !user.user_id) {
      throw new Error('User context is required to delete notifications');
    }
    
    const userNotifications = await this.getUserNotifications();
    
    // If there are no notifications, return early
    if (userNotifications.length === 0) {
      return true;
    }
    
    // Delete each notification
    for (const notification of userNotifications) {
      await this.repository.delete(notification.id);
    }
    
    // Broadcast an update after deleting all notifications
    try {
      const channel = new BroadcastChannel('notification-updates');
      channel.postMessage({ type: 'ALL_NOTIFICATIONS_CLEARED' });
      channel.close();
    } catch (error) {
      console.error('NotificationService: Error broadcasting ALL_NOTIFICATIONS_CLEARED:', error);
    }
    
    return true;
  }
  
  /**
   * Clean up old notifications based on retention policy
   * Keeps only the most recent MAX_NOTIFICATIONS notifications and
   * removes notifications older than NOTIFICATION_RETENTION_DAYS
   * @returns {Promise<number>} Number of notifications deleted
   */
  async cleanupNotifications() {
    const user = AuthService.getPostAuthData()?.userData;
    if (!user || !user.user_id) {
      throw new Error('User context is required to clean up notifications');
    }
    
    const userNotifications = await this.getUserNotifications();
    
    // If there are fewer notifications than the maximum, just check for old ones
    if (userNotifications.length <= MAX_NOTIFICATIONS) {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - NOTIFICATION_RETENTION_DAYS);
      const cutoffTimestamp = cutoffDate.getTime();
      
      const oldNotifications = userNotifications.filter(n => n.timestamp < cutoffTimestamp);
      
      // Delete old notifications
      for (const notification of oldNotifications) {
        await this.repository.delete(notification.id);
      }
      
      return oldNotifications.length;
    }
    
    // If there are more notifications than the maximum, sort by timestamp and keep only the most recent
    userNotifications.sort((a, b) => b.timestamp - a.timestamp);
    const notificationsToDelete = userNotifications.slice(MAX_NOTIFICATIONS);
    
    // Delete excess notifications
    for (const notification of notificationsToDelete) {
      await this.repository.delete(notification.id);
    }
    
    return notificationsToDelete.length;
  }
  
  /**
   * Get unread notifications count for the current user
   * @returns {Promise<number>}
   */
  async getUnreadCount() {
    const userNotifications = await this.getUserNotifications();
    return userNotifications.filter(n => !n.read).length;
  }
  
  /**
   * Check if desktop notifications are supported and permission is granted
   * @returns {string} 'granted', 'denied', 'default', or 'unsupported'
   */
  checkNotificationPermission() {
    if (!('Notification' in window)) {
      return 'unsupported';
    }
    
    return Notification.permission;
  }
  
  /**
   * Request permission for desktop notifications
   * @returns {Promise<string>} Permission status: 'granted', 'denied', or 'default'
   */
  async requestNotificationPermission() {
    if (!('Notification' in window)) {
      return 'unsupported';
    }
    
    try {
      const permission = await Notification.requestPermission();
      this.desktopNotificationsEnabled = permission === 'granted';
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'error';
    }
  }
  
  // Store active notifications by event type
  activeNotifications = new Map();

  /**
   * Show a desktop notification for a notification object
   * @param {Object} notification - The notification object
   * @returns {boolean} True if the notification was shown, false otherwise
   */
  showDesktopNotification(notification) {
    if (!notification) {
      console.error('Notification object is null or undefined');
      return false;
    }
    
    console.log('Attempting to show desktop notification:', notification);
    
    // Ensure notification has required fields before proceeding
    if (!notification.title) {
      console.error('Notification title is missing, assigning default');
      notification.title = 'Alert Notification';
    }
    
    if (!notification.message) {
      console.error('Notification message is missing, assigning default');
      notification.message = 'An alert has been triggered in DeepRDashboard';
    }
    
    // Validate the notification
    const { isValid } = validateNotification(notification);
    if (!isValid) {
      console.error('Invalid notification object, even after adding defaults');
      return false;
    }
    
    // Create a tag based on the event type and alert type for grouping similar notifications
    const sourceType = notification.source?.type || 'unknown';
    const eventType = notification.source?.data?.event || 'general';
    const tag = `${sourceType}-${eventType}`;
    
    // Store the current time to show in the notification
    const currentTime = new Date().toLocaleTimeString();
    
    // Check if we already have an active notification for this tag
    if (this.activeNotifications.has(tag)) {
      try {
        // Get the existing notification
        const existingNotification = this.activeNotifications.get(tag);
        
        // Close the existing notification
        existingNotification.close();
        
        console.log(`Closed existing notification for tag: ${tag}`);
      } catch (error) {
        console.warn(`Failed to close existing notification: ${error.message}`);
        // Continue even if closing fails
      }
    }
    
    // Check if desktop notifications are supported
    if (!('Notification' in window)) {
      console.error('Desktop notifications are not supported in this browser');
      return false;
    }
    
    // Check permission status
    const permission = Notification.permission;
    console.log('Current notification permission status:', permission);
    
    if (permission !== 'granted') {
      console.error('Desktop notifications permission not granted');
      
      // Try requesting permission again if it's not denied
      if (permission !== 'denied') {
        this.requestNotificationPermission().then(newPermission => {
          console.log('Updated permission after request:', newPermission);
          if (newPermission === 'granted') {
            // Try showing the notification again after permission is granted
            setTimeout(() => this.showDesktopNotification(notification), 500);
          }
        });
      }
      return false;
    }
    
    try {
      // Create a tag based on the event type and alert type for grouping similar notifications
      const sourceType = notification.source?.type || 'unknown';
      const eventType = notification.source?.data?.event || 'general';
      const tag = `${sourceType}-${eventType}`;
      
      // Get current time for the notification
      const currentTime = new Date().toLocaleTimeString();
      
      // Append the time to the message for clearer event sequence
      const enhancedMessage = `${notification.message} [${currentTime}]`;
      
      console.log('Creating desktop notification with:', {
        title: notification.title,
        body: enhancedMessage,
        icon: this.getSeverityIcon(notification.severity),
        tag: tag
      });
      
      // Create and show the notification
      const desktopNotification = new Notification(notification.title, {
        body: enhancedMessage,
        icon: this.getSeverityIcon(notification.severity),
        tag: tag, // Use our custom tag for grouping similar notifications
        requireInteraction: true, // Keep notification visible until user interacts with it
        renotify: true // Make the notification reappear even if using the same tag
      });
      
      // Add click handler to focus the window and mark as read
      desktopNotification.onclick = () => {
        console.log('Desktop notification clicked');
        window.focus();
        this.markAsRead(notification.id).catch(error => {
          console.error('Error marking notification as read:', error);
        });
        
        // Remove from active notifications when clicked
        this.activeNotifications.delete(tag);
      };
      
      // Track notification events
      desktopNotification.onshow = () => {
        console.log('Desktop notification shown to user');
      };
      
      desktopNotification.onerror = (error) => {
        console.error('Desktop notification error:', error);
        // Remove from active notifications on error
        this.activeNotifications.delete(tag);
      };
      
      // Store the notification in our active notifications map
      this.activeNotifications.set(tag, desktopNotification);
      
      console.log('Desktop notification successfully created and tracked');
      return true;
    } catch (error) {
      console.error('Error showing desktop notification:', error);
      return false;
    }
  }
  
  /**
   * Get an appropriate icon URL based on notification severity
   * @param {string} severity - The notification severity
   * @returns {string} URL to an appropriate icon
   */
  getSeverityIcon(severity) {
    // Default icons based on severity
    switch (severity) {
      case 'Critical':
        return '/icons/critical.png';
      case 'Warning':
        return '/icons/warning.png';
      case 'Info':
      default:
        return '/icons/info.png';
    }
  }
}

export default NotificationService;
